<template>
  <div class="user-page">
    <PageHeader :title="$t('messenger.messages')" icon="uil-comment-alt-lines" class="msg-page-header" />

    <div class="msg-layout" :class="{ 'msg-layout--detail-open': showDetail }">
              <!-- Chat List Panel -->
              <div class="msg-list-panel">
                <div class="msg-search">
                  <i class="icon-material-outline-search msg-search__icon"></i>
                  <input
                      id="autocomplete-input"
                      v-model="search"
                      :placeholder="$t('messenger.search')"
                      type="text"
                      class="msg-search__input"
                      @input="isTyping = true"
                  />
                </div>

                <template v-if="userListLoading">
                  <ul class="msg-items">
                    <li v-for="n in 5" :key="'skeleton_chat_' + n" class="skeleton-chat-item">
                      <div class="skeleton-chat-avatar"></div>
                      <div class="skeleton-chat-content">
                        <div class="skeleton-chat-name"></div>
                        <div class="skeleton-chat-message"></div>
                      </div>
                    </li>
                  </ul>
                </template>

                <template v-else>
                  <ul class="msg-items">
                    <li v-for="(item, key) in messengerSearchResults" :key="key"
                        class="msg-item"
                        @click="createMessageGroup(item)">
                      <div class="msg-item__avatar">
                        <UserProfileAvatar
                            :name="[item.name, item.surname].filter(Boolean).join(' ')"
                            :photo="item.photo_link"
                            class="msg-list-avatar"
                        />
                        <i :class="item.status ? 'status-online' : 'status-offline'" class="msg-item__status"/>
                      </div>
                      <div class="msg-item__content">
                        <h5 class="msg-item__name">{{ item.name + " " + item.surname }}</h5>
                      </div>
                    </li>
                    <li v-for="(item, index) in userList" :key="index"
                        class="msg-item"
                        :class="{ 'msg-item--active': item.chatId == activeChat.id }"
                        @click="openMessageGroup(item)">
                      <div class="msg-item__avatar">
                        <UserProfileAvatar
                            :name="item.chatName"
                            :photo="item.chatPhoto"
                            class="msg-list-avatar"
                        />
                        <i :class="item.chatUserStatus ? 'status-online' : 'status-offline'" class="msg-item__status"/>
                      </div>
                      <div class="msg-item__content">
                        <h5 class="msg-item__name">{{ item.chatName }}</h5>
                        <p v-if="item.chatLastMessage" class="msg-item__last-msg">{{ item.chatLastMessage }}</p>
                      </div>
                    </li>
                  </ul>
                </template>
              </div>

              <!-- Message Content Panel -->
              <div class="msg-detail-panel">
                <template v-if="(messageGroupLoading || messagesLoading) && currentPage < 2">
                  <div class="msg-detail__header">
                    <div class="skel skel--circle skel--avatar"></div>
                    <div class="skel-header-info">
                      <div class="skel skel--name"></div>
                      <div class="skel skel--status"></div>
                    </div>
                  </div>
                  <div class="skel-body">
                    <div class="skel skel--date-divider"></div>
                    <div v-for="n in 6" :key="'skeleton_msg_' + n"
                         class="skel-bubble" :class="n % 2 === 0 ? 'skel-bubble--me' : ''">
                      <div class="skel skel--msg" :style="{ width: [55,70,45,80,60,50][n-1] + '%' }"></div>
                    </div>
                  </div>
                  <div class="skel-reply">
                    <div class="skel skel--input"></div>
                    <div class="skel skel--btn"></div>
                  </div>
                </template>
                <template v-else-if="activeChat && activeChat.id">
                  <div class="msg-detail__header">
                    <div class="msg-detail__back" @click="onBackToList">
                      <i class="uil-arrow-left"></i>
                    </div>
                    <div class="msg-detail__user">
                      <div class="avatar-parent-child">
                        <UserProfileAvatar
                            :name="activeChat.name"
                            :photo="activeChat.photo"
                            class="msg-header-avatar"
                        />
                        <span :class="activeChat.status ? 'bg-success' : 'bg-offline'"
                              class="avatar-child avatar-badge"></span>
                      </div>
                      <div class="msg-detail__user-info">
                        <h4 class="msg-detail__user-name">{{ activeChat.name }}</h4>
                        <span class="msg-detail__user-status" :class="{ 'msg-detail__user-status--online': activeChat.status }">
                          {{ activeChat.status ? $t('general.online') : $t('general.offline') }}
                        </span>
                      </div>
                    </div>
                    <div class="msg-detail__actions">
                      <router-link :to="{ name: 'user.show', params: { id: activeChat.userId } }"
                                   class="msg-detail__action-btn" uk-tooltip="Profiline bak">
                        <i class="uil-user"></i>
                      </router-link>
                    </div>
                  </div>

                  <div id="messages" class="msg-detail__body" v-on:scroll="handleScroll">
                    <div class="message-time-sign">
                      <span>
                        <i v-if="currentPage > 1 && (messageGroupLoading || messagesLoading)"
                           class="fa fa-spinner fa-pulse"></i>
                      </span>
                    </div>

                    <template v-for="(group, groupIndex) in groupedMessages">
                      <div :key="'date_' + groupIndex" class="msg-date-divider">
                        <span class="msg-date-divider__label">{{ group.label }}</span>
                      </div>
                      <div v-for="msg in group.messages" :key="'msg_' + msg.id"
                           :class="msg.sender_user_id == userInfo.id ? 'me' : ''" class="message-bubble">
                        <div :id="'message_' + msg.id" class="message-bubble-inner">
                          <div class="message-text">
                            <p>{{ msg.message }}</p>
                            <span class="time">{{ formatTime(msg.created_at) }}</span>
                          </div>
                        </div>
                        <div class="clearfix"></div>
                      </div>
                    </template>

                    <div v-for="(item, index) in typingUsers" :key="index" class="message-bubble">
                      <div class="message-bubble-inner">
                        <div class="message-avatar">
                          <UserProfileAvatar
                              :name="item.fullName"
                              :photo="item.photo"
                              class="msg-typing-avatar"
                          />
                        </div>
                        <div class="message-text w-auto">
                          <div class="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                      <div class="clearfix"></div>
                    </div>
                    <span id="chat-box-area"></span>
                  </div>

                  <div class="msg-detail__reply">
                    <form class="msg-detail__reply-form">
                      <textarea v-model="message" :placeholder="$t('general.write_your_message')" cols="1"
                                data-autoresize rows="1" class="msg-detail__reply-input"
                                @keyup="messageTyping"></textarea>
                      <button class="msg-detail__reply-btn" type="button" @click.prevent="sendMessage">
                        {{ $t('general.send') }}
                      </button>
                    </form>
                  </div>
                </template>
                <div v-else class="msg-detail__empty">
                  <i class="uil-comment-alt-lines"></i>
                  <p>{{ company?.name }}</p>
                </div>
              </div>
            </div>
  </div>
</template>

<script>
import Vue from 'vue'
import messengerModule, {
  CREATE_MESSAGE,
  CREATE_MESSAGE_GROUP,
  GET_MESSAGE_GROUPS,
  GET_MESSAGES,
  GET_SEARCH,
  MESSAGE,
  MESSAGE_GROUP_LIST,
  MESSAGE_GROUP_LOADING,
  MESSAGES,
  MESSAGES_LOADING,
  MODULE_NAME,
  SEARCH,
  SET_MESSAGE_GROUP_LIST,
  SEARCH_LOADING,
  SET_USER_LIST
} from "@/core/services/store/messenger.module";
import store from "@/core/services";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";

import Echo from 'laravel-echo';
import _ from "lodash";
import company_module, {
  MODULE_NAME as COMPANY_MODULE_NAME,
  ONLINE_USERS,
  SELF as COMPANY_SELF,
} from "@/core/services/store/company.module";
import { GET_ITEMS } from "@/core/services/store/REST.module";
import moment from "moment-timezone";
import EventBus from '@/view/components/event-bus'

window.io = require("socket.io-client");
const _MODULE_NAME = MODULE_NAME;
const _COMPANY_MODULE_NAME = COMPANY_MODULE_NAME;
import { getSubCompanyId } from "@/core/services/store.js";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";

export default {
  name: "Messages",
  components: { DefaultLoading, UserProfileAvatar, PageHeader },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_MODULE_NAME, messengerModule);
    registerStoreModule(_COMPANY_MODULE_NAME, company_module);
  },
  data() {
    return {
      chatUsers: [],
      typingUsers: [],
      messageGroupUrlList: 'api/message-groups',
      userListUrl: 'api/message-user-list',
      createMessageGroupUrl: 'api/store-group',
      createMessageUrl: 'api/send-message',
      messagesUrl: 'api/get-messages',
      messagesCountUrl: "api/user-messages-count",
      search: '',
      isTyping: false,
      showDetail: false,
      chatMessages: [],
      currentPage: 1,
      lastPage: 0,
      currentGroupId: null, // Hangi chat grubunun yüklendiğini takip et
      userListLoadingLocal: true, // Sadece ilk yüklemede true
      isLoadingAllMessages: false, // Tüm mesajlar yüklenirken scroll'u engelle
      activeChat: {
        id: null,
        name: '',
        status: '',
        photo: '',
      },
      message: null,
      echoServer: null
    };
  },
  computed: {
    onlineUsers() {
      return store.getters[COMPANY_MODULE_NAME + "/" + ONLINE_USERS];
    },
    company() {
      return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_SELF];
    },
    userInfo() {
      return this.authUser;
    },
    messengerSearchResults() {
      let items = store.getters[_MODULE_NAME + "/" + SEARCH];
      if (!this.search || !items) return [];
      items.forEach(subItem => {
        subItem.status = this.onlineUsers.find(onlineUser => onlineUser.user == subItem.id && onlineUser.status) ? true : false
      })
      return items
    },
    userList() {
      let userLists = store.getters[_MODULE_NAME + "/" + MESSAGE_GROUP_LIST];
      let users = [];
      if (userLists && !Array.isArray(userLists)) {
        let newUserList = []
        for (const [key, value] of Object.entries(userLists)) {
          newUserList.push(value)
        }

        userLists = newUserList;
      }
      if (!userLists || !userLists.length || this.search) return users;
      userLists.forEach(item => {
        if (item.users.length == 2) {
          item.users.forEach(subItem => {
            if (subItem.id != this.userInfo.id) {
              users.push({
                chatId: item.id,
                chatUserId: subItem.id,
                chatName: subItem.fullname,
                chatPhoto: subItem.photo_link,
                chatLastMessage: item.messages.length && item.messages[0] && item.messages[0].hasOwnProperty('message') ? item.messages[0].message : "-",
                chatUserStatus: this.onlineUsers.find(onlineUser => onlineUser.user == subItem.id && onlineUser.status) ? true : false
              })
            }
          })
        } else {
          item.users.forEach(subItem => {
            if (subItem.id != this.userInfo.id) {
              users.push({
                chatId: item.id,
                chatUserId: subItem.id,
                chatName: item.name,
                chatPhoto: subItem.photo_link,
                chatLastMessage: item.messages.length && item.messages[0] && item.messages[0].hasOwnProperty('message') ? item.messages[0].message : "-",
                chatUserStatus: null,
              })
            }
          })
        }
      })
      return users
    },
    userListLoading() {
      return this.userListLoadingLocal;
    },
    messageGroupLoading() {
      return store.getters[_MODULE_NAME + "/" + MESSAGE_GROUP_LOADING];
    },
    newMessage() {
      return store.getters[_MODULE_NAME + "/" + MESSAGE];
    },
    messages() {
      let items = store.getters[_MODULE_NAME + "/" + MESSAGES];
      let self = this;
      if (!items) return [];
      this.chatUsers = items.groups.users;
      let messages = items.messages.data;
      this.lastPage = items.messages.last_page;
      messages.forEach(item => {
        item.user = self.chatUsers.find(subItem => subItem.id === item.sender_user_id);
      });

      // Eğer farklı bir chat grubu yükleniyorsa, mesajları temizle
      if (this.currentGroupId !== null && this.currentGroupId !== items.groups.id) {
        this.chatMessages = [];
        this.currentGroupId = items.groups.id;
      }

      // Mesajları created_at'e göre sırala (eski->yeni)
      const sortedMessages = [...messages].sort((a, b) => {
        const dateA = new Date(a.created_at || a.date || 0);
        const dateB = new Date(b.created_at || b.date || 0);
        return dateA - dateB;
      });

      if (this.chatMessages.length > 0 && this.currentGroupId === items.groups.id) {
        // Mevcut mesaj ID'lerini al (duplicate kontrolü için)
        const existingMessageIds = new Set(this.chatMessages.map(msg => msg.id));
        
        // Yeni mesajları filtrele (daha önce eklenmemiş olanları)
        const newMessages = sortedMessages.filter(msg => !existingMessageIds.has(msg.id));
        
        // Yeni mesajları mevcut mesajlara ekle ve tekrar sırala
        if (newMessages.length > 0) {
          this.chatMessages = [...this.chatMessages, ...newMessages].sort((a, b) => {
            const dateA = new Date(a.created_at || a.date || 0);
            const dateB = new Date(b.created_at || b.date || 0);
            return dateA - dateB;
          });
        }
      } else {
        // İlk yükleme veya yeni chat grubu - mesajları doğru sırada kaydet
        this.chatMessages = sortedMessages;
        this.currentGroupId = items.groups.id;
      }
      return this.chatMessages;
    },
    messagesLoading() {
      return store.getters[_MODULE_NAME + "/" + MESSAGES_LOADING];
    },
    groupedMessages() {
      if (!this.messages || !this.messages.length) return [];
      const groups = [];
      let currentDateKey = null;
      const today = moment().tz(this.timeZone()).startOf('day');
      const yesterday = moment().tz(this.timeZone()).subtract(1, 'day').startOf('day');

      this.messages.forEach((msg) => {
        const msgDate = moment.tz(msg.created_at, "UTC").tz(this.timeZone()).startOf('day');
        const dateKey = msgDate.format('YYYY-MM-DD');

        if (dateKey !== currentDateKey) {
          currentDateKey = dateKey;
          let label;
          if (msgDate.isSame(today, 'day')) {
            label = this.$t('general.today') || 'Bugün';
          } else if (msgDate.isSame(yesterday, 'day')) {
            label = this.$t('general.yesterday') || 'Dün';
          } else {
            label = msgDate.locale(this.secureLocalStorage("language")).format('DD MMMM YYYY');
          }
          groups.push({ label, messages: [] });
        }
        groups[groups.length - 1].messages.push(msg);
      });
      return groups;
    },
  },
  methods: {
    onBackToList() {
      this.showDetail = false;
      if (this.$route.params.id) {
        this.$router.replace({ name: 'User.messages' });
      }
    },
    formatTime(date) {
      return moment.tz(date, "UTC").tz(this.timeZone()).format("HH:mm");
    },
    handleScroll: function (e) {
      let self = this;
      let left = e.target.scrollLeft;
      let top = e.target.scrollTop;

      let percentValue = e.target.scrollTop * 100 / e.target.scrollHeight;
      if (percentValue <= 10) {
        if (this.currentPage != this.lastPage) {
          const el = document.querySelector('#messages');
          el.scrollLeft = left;
          el.scrollTop = top + 100;
        }
        if (!self.messagesLoading) {
          self.getMessages();
        }
      }
    },
    messageTyping() {
      let conversationMessageId = this.activeChat.id;
      let username = [{
        photo: this.userInfo.photo,
        fullName: this.userInfo.fullname,
      }];
      this.echoServer.join(`chat-room-${conversationMessageId}`)
          .whisper('typing', username);
    },
    messengerSearch() {
      if (!this.search) {
        store.commit(_MODULE_NAME + '/' + SET_USER_LIST, null);
      } else {
        this.$store.dispatch(_MODULE_NAME + '/' + GET_SEARCH, {
          url: this.userListUrl,
          filters: {
            search: this.search,
          }
        })
      }
    },
    getMessageGroups() {
      return this.$store.dispatch(_MODULE_NAME + '/' + GET_MESSAGE_GROUPS, {
        url: this.messageGroupUrlList,
        filters: {}
      }).then(() => {
        // İlk yükleme tamamlandıktan sonra loading state'ini false yap
        this.userListLoadingLocal = false;

        // URL'de id varsa ve henüz aktif chat yoksa, ilgili chat'i aç
        const routeId = this.$route.params.id;
        if (routeId && !this.activeChat.id) {
          const match = this.userList.find((item) => item.chatId == routeId);
          if (match) {
            this.openMessageGroup(match);
          }
        }
      });
    },
    createMessageGroup(user) {
      let userId = user['id'];
      let users = [userId];

      this.search = '';
      let userCheck = this.userList.find(item => item.chatUserId == userId);
      if (userCheck) {
        this.openMessageGroup(userCheck);
      } else {
        this.$store.dispatch(_MODULE_NAME + '/' + CREATE_MESSAGE_GROUP, {
          url: this.createMessageGroupUrl,
          contents: {
            users: users
          }
        }).then(response => {
          if (response.status) {
            let responeData = response.data;
            let data = {
              chatId: responeData.id,
              chatUserId: user.id,
              chatName: user.fullname,
              chatPhoto: user.photo_link,
              chatUserStatus: this.onlineUsers.find(onlineUser => onlineUser.user == user.id && onlineUser.status) ? true : false
            }
            this.getMessageGroups();
            this.openMessageGroup(data);
          }
        })
      }
    },
    sendMessage() {
      if (!this.message || !this.message.trim()) {
        return;
      }

      let content = {
        "message_group_id": this.activeChat.id,
        "sender_user_id": this.userInfo.id,
        "message": this.message
      }

      const isFirstInList = this.userList.length > 0 && this.userList[0].chatId === this.activeChat.id;

      this.$store.dispatch(_MODULE_NAME + '/' + CREATE_MESSAGE, {
        url: this.createMessageUrl,
        contents: content
      }).then(() => {
        this.message = null;
        
        // Sol menüdeki son mesajı güncellemek için message groups'u yeniden yükle
        this.debouncedGetMessageGroups();
        
        if (!isFirstInList) {
          this.moveChatToTop(this.activeChat.id);
        }
        
        setTimeout(() => {
          this.scrollToEnd();
        }, 150);
      });
    },
    moveChatToTop(chatId) {
      let userLists = store.getters[_MODULE_NAME + "/" + MESSAGE_GROUP_LIST];
      if (!userLists) return;

      let userListArray = [];
      if (!Array.isArray(userLists)) {
        for (const [key, value] of Object.entries(userLists)) {
          userListArray.push(value);
        }
      } else {
        userListArray = [...userLists];
      }

      let chatIndex = userListArray.findIndex(item => item.id === chatId);
      if (chatIndex > 0) {
        let chatItem = userListArray.splice(chatIndex, 1)[0];
        userListArray.unshift(chatItem);

        let updatedList = Array.isArray(userLists) ? userListArray : Object.fromEntries(
          userListArray.map((item, index) => [index, item])
        );

        store.commit(_MODULE_NAME + '/' + SET_MESSAGE_GROUP_LIST, updatedList);
      }
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const messagesContainer = document.getElementById("messages");
        if (messagesContainer) {
          const scrollHeight = messagesContainer.scrollHeight;
          messagesContainer.scrollTo({
            top: scrollHeight + 1000,
            behavior: 'smooth'
          });
        }
      });
    },
    openMessageGroup(item) {
      // Önceki chat'in listener'larını temizle
      if (this.activeChat && this.activeChat.id) {
        this.echoServer.leave(`chat-room-${this.activeChat.id}`);
        this.echoServer.leave(`Chat.${this.activeChat.id}`);
      }

      this.showDetail = true;

      // URL'yi güncelle
      if (this.$route.params.id != item.chatId) {
        this.$router.replace({ name: 'User.messages', params: { id: item.chatId } });
      }

      // Yeni chat açıldığında eski mesajları temizle
      this.chatMessages = [];
      this.currentPage = 1;
      this.currentGroupId = null; // Group ID'yi sıfırla, böylece messages computed property yeni grup olarak algılasın
      this.activeChat = {
        id: item.chatId,
        status: this.onlineUsers.find(onlineUser => onlineUser.user == item.chatUserId && onlineUser.status) ? true : false,
        userId: item.chatUserId,
        name: item.chatName,
        photo: item.chatPhoto,
      }
      let self = this;
      self.typingUsers = [];
      this.echoServer.join(`chat-room-${item.chatId}`)
          .listenForWhisper('typing', (data) => {
            self.typingUsers = data;
            self.scrollToEnd();
            setTimeout(function () {
              self.typingUsers = [];
            }, 2000);
          });

      let messageGroupId = item.chatId;

      this.echoServer.private(`Chat.${messageGroupId}`).listen('.message.send', (payload) => {
        if (payload && payload.message && payload.message.sender_user_id != self.userInfo.id) {
          let tmpMessage = Object.assign({}, payload.message);
          let user = self.chatUsers.find(subItem => subItem.id === tmpMessage.sender_user_id);
          tmpMessage = Object.assign({}, tmpMessage, { user: user });

          // Duplicate kontrolü yap ve Vue reactive array'e ekle
          if (!self.chatMessages.find(m => m.id === tmpMessage.id)) {
            self.chatMessages.push(tmpMessage);
            self.$nextTick(() => {
              self.scrollToEnd();
            });
          }

          // Sol menüdeki son mesajı güncellemek için message groups'u yeniden yükle
          self.debouncedGetMessageGroups();
        }
      });

      this.$store.dispatch(_MODULE_NAME + '/' + GET_MESSAGES, {
        url: this.messagesUrl,
        filters: {
          user_id: this.userInfo.id,
          group_id: item.chatId,
          page: this.currentPage
        }
      }).then(() => {
        // İlk sayfa yüklendikten sonra lastPage bilgisi messages computed property'de güncellenir
        // lastPage'in güncellenmesini beklemek için bir sonraki tick'te tüm sayfaları yükle
        this.$nextTick(() => {
          this.loadAllMessages();
        });
      });
      this.getMessagesCount();
    },
    getMessages() {
      if (this.currentPage < this.lastPage && this.activeChat.id) {
        this.currentPage += 1;
        return this.$store.dispatch(_MODULE_NAME + '/' + GET_MESSAGES, {
          url: this.messagesUrl,
          filters: {
            user_id: this.userInfo.id,
            group_id: this.activeChat.id,
            page: this.currentPage
          }
        });
      }
      return Promise.resolve();
    },
    async loadAllMessages() {
      // Scroll'u engelle - tüm mesajlar yüklenene kadar
      this.isLoadingAllMessages = true;

      // lastPage değerinin güncellenmesini bekle (messages computed property çalışsın)
      await this.$nextTick();

      // Eğer sadece 1 sayfa varsa direkt scroll yap
      if (this.lastPage <= 1) {
        this.isLoadingAllMessages = false;
        this.scrollToEnd();
        return;
      }

      // Tüm sayfaları sırayla yükle
      while (this.currentPage < this.lastPage) {
        await this.getMessages();
        // Her sayfa yüklendikten sonra DOM güncellemesi için bekle
        await this.$nextTick();
      }

      // Tüm mesajlar yüklendikten sonra scroll'u aç ve en alta scroll yap
      this.isLoadingAllMessages = false;
      await this.$nextTick();
      this.scrollToEnd();
    },
    getMessagesCount() {
      let self = this;
      self.$store
          .dispatch(GET_ITEMS, {
            url: this.messagesCountUrl,
            filters: {},
            acceptPromise: false,
          })
          .then((response) => {
            EventBus.$emit('message_count')
            self.itemCount = response.data;
          });
    },
  },
  mounted() {
    // Message groups'u güncellemek için debounce ekle (skeleton loading'i önlemek için)
    this.debouncedGetMessageGroups = _.debounce(this.getMessageGroups, 300);
    
    this.getMessageGroups();
    this.echoServer = new Echo({
      broadcaster: "socket.io",
      host: process.env.VUE_APP_WEBSOCKET_URL,
      auth: {
        headers: {
          Authorization: "Bearer " + this.secureLocalStorage("token"),
          Company: getSubCompanyId?.value,
        },
      },
      forceTLS: false,
      disableStats: true,
      encrypted: false,
      enabledTransports: ['ws', 'wss'],
    });
    let message = this.$route.params.message
    if (message && message.id) {
      let messageSetting = {

        chatId: message.message_group_id,
        chatUserId: message.sender_user_id,
        chatName: message.user.fullname,
        chatPhoto: message.user.photo_link,
        chatLastMessage: message.message,
        chatUserStatus: this.onlineUsers.find(onlineUser => onlineUser.user == message.user.id && onlineUser.status) ? true : false
      }
      this.openMessageGroup(messageSetting)
    }
    this.getMessagesCount();

    if (this.activeChat?.id) {
      this.$nextTick(() => {
        this.scrollToEnd();
      });
    }
  },
  watch: {
    search: _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    isTyping: function (value) {
      if (!value) {
        this.messengerSearch();
      }
    },
    messages: function (newMessages, oldMessages) {
      // Tüm mesajlar yüklenirken scroll yapma - loadAllMessages sonunda yapılacak
      if (this.isLoadingAllMessages) {
        return;
      }

      if (newMessages && newMessages.length > 0) {
        if (this.currentPage === 1 || !oldMessages || newMessages.length > oldMessages.length) {
          this.$nextTick(() => {
            this.scrollToEnd();
          });
        }
      }
    },
    newMessage: function (message) {
      if (message) {
        let tmpMessage = Object.assign({}, message);
        let user = this.chatUsers.find(subItem => subItem.id === tmpMessage.sender_user_id);
        tmpMessage = Object.assign({}, tmpMessage, { user: user });

        // Duplicate kontrolü yap ve Vue reactive array'e ekle
        if (!this.chatMessages.find(m => m.id === tmpMessage.id)) {
          this.chatMessages.push(tmpMessage);
          this.$nextTick(() => {
            this.scrollToEnd();
          });
        }

        // Sol menüdeki son mesajı güncellemek için message groups'u yeniden yükle
        this.debouncedGetMessageGroups();
      }
    }
  }
};
</script>

<style scoped>
/* Layout */
.msg-layout {
  display: flex;
  gap: 20px;
  min-height: 500px;
}

.msg-list-panel {
  width: 380px;
  min-width: 380px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.msg-detail-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search */
.msg-search {
  position: relative;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.msg-search__icon {
  position: absolute;
  left: 28px;
  top: 40%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  pointer-events: none;
}

.msg-search__input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.msg-search__input::placeholder {
  color: #94a3b8;
}

.msg-search__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  background: #fff;
}

/* Chat List Items */
.msg-items {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.msg-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s ease;
}

.msg-item:hover {
  background: #f8fafc;
}

.msg-item--active {
  background: #eff6ff;
}

.msg-item--active:hover {
  background: #eff6ff;
}

.msg-item__avatar {
  position: relative;
  flex-shrink: 0;
}

.msg-list-avatar {
  width: 38px;
  height: 38px;
  min-width: 38px;
  font-size: 13px;
}

.msg-item__status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
}

.msg-item__status.status-online {
  background: #22c55e;
}

.msg-item__status.status-offline {
  background: #94a3b8;
}

.msg-item__content {
  flex: 1;
  min-width: 0;
}

.msg-item__name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-item__last-msg {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Detail Panel - Header */
.msg-detail__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.msg-detail__back {
  display: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  color: #2563eb;
  font-size: 20px;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
  margin-right: 2px;
}

.msg-detail__back:hover {
  background: #eff6ff;
}

.msg-detail__user {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.msg-header-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  font-size: 13px;
}

.msg-detail__user .avatar-child {
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

.msg-typing-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  font-size: 11px;
}

.msg-detail__user-info {
  min-width: 0;
}

.msg-detail__user-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-detail__user-status {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.2;
}

.msg-detail__user-status--online {
  color: #22c55e;
}

.msg-detail__actions {
  flex-shrink: 0;
  margin-left: auto;
}

.msg-detail__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  color: #475569;
  font-size: 18px;
  transition: all 0.2s ease;
  text-decoration: none;
}

.msg-detail__action-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

/* Detail Panel - Body (messages) */
.msg-detail__body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  max-height: 50vh;
}

/* Detail Panel - Reply */
.msg-detail__reply {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.msg-detail__reply-form {
  display: flex;
  align-items: center;
  gap: 12px;
}

.msg-detail__reply-input {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
  outline: none;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.msg-detail__reply-input::placeholder {
  color: #94a3b8;
}

.msg-detail__reply-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.msg-detail__reply-btn {
  flex-shrink: 0;
  padding: 10px 20px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.msg-detail__reply-btn:hover {
  background: #1d4ed8;
}

/* Detail Panel - Empty */
.msg-detail__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #94a3b8;
}

.msg-detail__empty i {
  font-size: 48px;
  opacity: 0.4;
}

.msg-detail__empty p {
  font-size: 16px;
  margin: 0;
}

/* Date Divider */
.msg-date-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
}

.msg-date-divider__label {
  display: inline-block;
  padding: 4px 16px;
  background: #e2e8f0;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  letter-spacing: 0.01em;
}

/* Message Bubble Overrides */
.msg-detail__body :deep(.message-bubble) {
  margin-bottom: 4px;
}

.msg-detail__body :deep(.message-bubble .message-avatar) {
  display: none;
}

.msg-detail__body :deep(.message-bubble .message-text) {
  margin-left: 0;
  padding: 6px 12px;
  border-radius: 8px;
  line-height: 20px;
  display: inline-flex;
  align-items: flex-end;
  gap: 10px;
  max-width: 75%;
}

.msg-detail__body :deep(.message-bubble .message-text p) {
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  padding: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.msg-detail__body :deep(.message-bubble .message-text:before) {
  content: "";
  width: 0;
  height: 0;
  position: absolute;
  bottom: 0;
  top: auto;
  left: -7px;
  right: auto;
  border-top: 20px solid transparent;
  border-bottom: 0 solid transparent;
  border-right: 20px solid #f4f4f4;
  border-left: none;
}

.msg-detail__body :deep(.message-bubble.me .message-text:before) {
  left: auto;
  right: -6px;
  border-top: 20px solid transparent;
  border-bottom: 0 solid transparent;
  border-left: 20px solid #93949c;
  border-right: none;
}

.msg-detail__body :deep(.message-bubble.me .message-text) {
  margin-right: 0;
  margin-left: auto;
}

/* Time */
.msg-detail__body :deep(.time) {
  display: inline;
  margin: 0;
  padding: 0;
  border: none;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  top: 3px;
}

.msg-detail__body :deep(.message-bubble:not(.me) .time) {
  color: #94a3b8;
}

.bg-offline {
  background-color: #c0c0c0 !important;
}

/* Skeleton base */
.skel {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skel-loading 1.5s infinite;
  border-radius: 4px;
}

.skel--circle { border-radius: 50%; }

/* Skeleton - Chat list */
.skeleton-chat-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.skeleton-chat-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skel-loading 1.5s infinite;
  margin-right: 12px;
  flex-shrink: 0;
}

.skeleton-chat-content {
  flex: 1;
  min-width: 0;
}

.skeleton-chat-name {
  height: 14px;
  width: 60%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skel-loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-chat-message {
  height: 12px;
  width: 80%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skel-loading 1.5s infinite;
  border-radius: 4px;
}

/* Skeleton - Detail header */
.skel--avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.skel-header-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skel--name {
  width: 120px;
  height: 16px;
}

.skel--status {
  width: 60px;
  height: 10px;
}

/* Skeleton - Message body */
.skel-body {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skel--date-divider {
  width: 80px;
  height: 24px;
  border-radius: 8px;
  align-self: center;
  margin-bottom: 8px;
}

.skel-bubble {
  display: flex;
}

.skel-bubble--me {
  justify-content: flex-end;
}

.skel--msg {
  height: 34px;
  border-radius: 8px;
}

/* Skeleton - Reply bar */
.skel-reply {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.skel--input {
  flex: 1;
  height: 40px;
  border-radius: 8px;
}

.skel--btn {
  width: 70px;
  height: 40px;
  border-radius: 8px;
}

@keyframes skel-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive - Tablet */
@media (max-width: 960px) {
  .msg-list-panel {
    width: 320px;
    min-width: 320px;
  }
}

/* Responsive - Mobile */
@media (max-width: 768px) {
  .msg-page-header {
    display: none;
  }

  .msg-layout {
    gap: 0;
    position: relative;
    min-height: calc(100vh - 90px);
  }

  .msg-list-panel {
    width: 100%;
    min-width: 100%;
    border-radius: 0;
    border: none;
  }

  .msg-search {
    padding: 8px 16px;
    border-bottom: none;
  }

  .msg-search__icon {
    left: 28px;
    transform: translateY(-50%);
    font-size: 16px;
    color: #8e8e93;
  }

  .msg-search__input {
    min-height: 48px;
    padding: 0 16px 0 40px;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    background: #f2f2f7;
  }

  .msg-search__input::placeholder {
    color: #8e8e93;
  }

  .msg-search__input:focus {
    border-color: transparent;
    box-shadow: none;
    background: #e8e8ed;
  }

  .msg-item {
    min-height: 56px;
    padding: 12px 16px;
  }

  .msg-item:hover {
    background: transparent;
  }

  .msg-item:active {
    background: #f1f5f9;
  }

  .msg-item--active:active {
    background: #eff6ff;
  }

  .msg-item__name {
    font-size: 16px;
  }

  .msg-item__last-msg {
    font-size: 14px;
  }

  .msg-detail-panel {
    border-radius: 0;
    border: none;
    height: calc(100vh - 90px);
  }

  .msg-detail__header {
    padding: 8px 16px;
    gap: 12px;
  }

  .msg-detail__back {
    display: inline-flex;
    width: 44px;
    height: 44px;
    font-size: 20px;
    margin-right: 0;
    border-radius: 50%;
  }

  .msg-detail__back:hover {
    background: transparent;
  }

  .msg-detail__back:active {
    background: #f1f5f9;
  }

  .msg-detail__user {
    gap: 8px;
  }

  .msg-detail__user-name {
    font-size: 16px;
  }

  .msg-detail__user-status {
    font-size: 12px;
  }

  .msg-detail__action-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
    border-radius: 50%;
  }

  .msg-detail__action-btn:hover {
    background: transparent;
  }

  .msg-detail__action-btn:active {
    background: #f1f5f9;
  }

  /* List/detail toggle */
  .msg-layout .msg-detail-panel {
    display: none;
  }

  .msg-layout--detail-open .msg-list-panel {
    display: none;
  }

  .msg-layout--detail-open .msg-detail-panel {
    display: flex;
  }

  /* Body & Reply — mobilde tam yükseklik */
  .msg-detail__body {
    flex: 1;
    padding: 16px;
    max-height: none;
  }

  .msg-detail__reply {
    position: sticky;
    bottom: 0;
    padding: 8px 16px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #e2e8f0;
  }

  .msg-detail__reply-form {
    gap: 8px;
    align-items: flex-end;
  }

  .msg-detail__reply-input {
    height: 44px;
    min-height: 44px;
    padding: 0 16px;
    border: none;
    border-radius: 20px;
    font-size: 16px;
    line-height: 44px;
    background: #f2f2f7;
    resize: none;
  }

  .msg-detail__reply-input::placeholder {
    color: #8e8e93;
  }

  .msg-detail__reply-input:focus {
    border: none;
    box-shadow: none;
    background: #e8e8ed;
  }

  .msg-detail__reply-btn {
    min-width: 44px;
    height: 44px;
    padding: 0 16px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    border-radius: 20px;
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }

  .msg-detail__reply-btn:hover {
    background: #2563eb;
  }

  .msg-detail__reply-btn:focus {
    outline: none;
    border: none;
    box-shadow: none;
  }

  .msg-detail__reply-btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  /* Message bubbles — native style */
  .msg-detail__body :deep(.message-bubble .message-text) {
    max-width: 85%;
    padding: 8px 16px;
    border-radius: 16px;
  }

  .msg-detail__body :deep(.message-bubble .message-text p) {
    font-size: 16px;
    line-height: 1.4;
  }

  .msg-detail__body :deep(.time) {
    font-size: 12px;
  }

  .msg-detail__empty i {
    font-size: 40px;
  }

  .msg-detail__empty p {
    font-size: 14px;
  }
}

/* Responsive - Small Mobile */
@media (max-width: 480px) {
  .msg-search {
    padding: 8px 12px;
  }

  .msg-item {
    padding: 12px;
    gap: 8px;
  }

  .msg-item__last-msg {
    font-size: 12px;
  }

  .msg-detail__header {
    padding: 8px 12px;
  }

  .msg-detail__body {
    padding: 12px;
  }
}
</style>
